import React, { useState } from 'react';
import { DateTime } from 'luxon';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import IconButton from '@mui/material/IconButton';
import Collapse from '@mui/material/Collapse';
import Box from '@mui/material/Box';
import CircleIcon from '@mui/icons-material/Circle';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Treatment } from '../../routes/userprofile'; //
import JSZip from 'jszip';

interface TreatmentTimelineProps {
  data: Treatment[];
}

const TreatmentTimeline: React.FC<TreatmentTimelineProps> = ({ data }) => {
  const [expandedYear, setExpandedYear] = useState<string | null>(null);
  const [expandedMonth, setExpandedMonth] = useState<string | null>(null);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [expandedRows, setExpandedRows] = useState<{ [key: number]: boolean }>({}); // Track expanded rows

  // Helper function to add suffix to day numbers
  const getDayWithSuffix = (day: number) => {
    const suffixes = ['th', 'st', 'nd', 'rd'];
    const value = day % 100;
    return `${day}${suffixes[(value - 20) % 10] || suffixes[value] || suffixes[0]}`;
  };

  // Group treatments by year, month, and day
  const groupedData = data.reduce((acc: any, treatment) => {

    if (!treatment.startTime) return acc;

    if (!DateTime.isDateTime(treatment.startTime)) {
     if (typeof treatment.startTime === "string") {
        // ISO 8601 string
        treatment.startTime = DateTime.fromISO(treatment.startTime);
      } else if (typeof treatment.startTime === "number") {
        // Unix timestamp in seconds
        treatment.startTime = DateTime.fromSeconds(treatment.startTime);
      } else {
        console.warn("Unrecognized format for treatment.startTime:", treatment.startTime);
      }
    }
    
    const year = treatment.startTime.year;
    const month = treatment.startTime.month;
    const day = treatment.startTime.day;
    if (!acc[year]) acc[year] = { total: 0, months: {} };
    if (!acc[year].months[month]) acc[year].months[month] = { total: 0, days: {} };
    if (!acc[year].months[month].days[day]) acc[year].months[month].days[day] = { total: 0, treatments: [] };

    acc[year].total += 1;
    acc[year].months[month].total += 1;
    acc[year].months[month].days[day].total += 1;
    acc[year].months[month].days[day].treatments.push(treatment);

    return acc;
  }, {});

  const toggleRow = (index: number) => {
    setExpandedRows((prev) => ({
      ...prev,
      [index]: !prev[index], // Toggle expanded state for the specific row
    }));
  };

  const filteredData = selectedDay
    ? groupedData[selectedDay.split('-')[0]]?.months[selectedDay.split('-')[1]]?.days[selectedDay.split('-')[2]]
        ?.treatments || []
    : [];

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button className="btn btn-sm btn-outline" onClick={() => downloadTreatmentsZip(data)}>
          Export treatments
        </button>
      </div>
      <div className="mb-4">
        {Object.keys(groupedData).map((year) => (
          <Accordion
            key={year}
            expanded={expandedYear === year}
            onChange={() => setExpandedYear(expandedYear === year ? null : year)}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <div className="flex items-center">
                <CircleIcon className="text-purple-500 mr-2" />
                <p className="font-bold">{year}</p>
                <p className="ml-2 text-sm text-gray-500">({groupedData[year].total} treatments)</p>
              </div>
            </AccordionSummary>
            <AccordionDetails>
                      {/* DEBUG */}

              {Object.keys(groupedData[year].months).map((month) => (
                
                <Accordion
                  key={month}
                  expanded={expandedMonth === `${year}-${month}`}
                  onChange={() =>
                    setExpandedMonth(expandedMonth === `${year}-${month}` ? null : `${year}-${month}`)
                  }
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <div className="flex items-center">
                      <CircleIcon className="text-purple-500 mr-2" />
                      <p className="font-bold">
                        {DateTime.fromObject({ month: Number(month) }).toFormat('MMMM')}
                      </p>
                      <p className="ml-2 text-sm text-gray-500">
                        ({groupedData[year].months[month].total} treatments)
                      </p>
                    </div>
                  </AccordionSummary>
                  <AccordionDetails>
                            {/* DEBUG */}

                    {Object.keys(groupedData[year].months[month].days).map((day) => {
                      
                      const dayString = `${year}-${month}-${day}`;
                      const isSelectedDay = selectedDay === dayString;
                      return (
                        <Accordion
                          key={day}
                          expanded={isSelectedDay}
                          onChange={() => setSelectedDay(isSelectedDay ? null : dayString)}
                        >
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <div className="flex items-center">
                              <CircleIcon className="text-purple-500 mr-2" />
                              <p className="font-bold">{getDayWithSuffix(Number(day))}</p>
                              <p className="ml-2 text-sm text-gray-500">
                                ({groupedData[year].months[month].days[day].total} treatments)
                              </p>
                            </div>
                          </AccordionSummary>
                          <AccordionDetails>
                            {isSelectedDay && (
                              <div>
                                {/* Treatment Details */}
                                <div className="mb-4">
                                  <h3 className="text-lg font-bold">Treatment Details</h3>
                                   
                                </div>

                                {/* Timeline Section */}
                                <TableContainer>
                                  <Table>
                                    <TableHead>
                                      <TableRow>
                                        <TableCell>Start Time</TableCell>
                                        <TableCell>Length (mins)</TableCell>
                                        <TableCell>Device ID</TableCell>
                                        <TableCell>Temperature (Pre/Post)</TableCell>
                                        <TableCell>Pain Score (Pre/Post)</TableCell>
                                        <TableCell>Device TN</TableCell>
                                        <TableCell>Platform</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {filteredData.map((treatment: Treatment, index: number) => (
                                        <React.Fragment key={index}>
                                          <TableRow>
                                            <TableCell>{treatment.startTime.toFormat('h:mm a')}</TableCell>
                                            <TableCell>{treatment.length}</TableCell>
                                            <TableCell>{treatment.deviceId}</TableCell>
                                            <TableCell>
                                              {treatment.temperaturePre}°C / {treatment.temperaturePost}°C
                                            </TableCell>
                                            <TableCell>
                                              {treatment.painScorePre} / {treatment.painScorePost}
                                            </TableCell>
                                            <TableCell>{treatment.deviceTN}</TableCell>
                                            <TableCell>{treatment.platform}</TableCell>

                                          </TableRow>
                                        </React.Fragment>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                              </div>
                            )}
                          </AccordionDetails>
                        </Accordion>
                      );
                    })}
                  </AccordionDetails>
                </Accordion>
              ))}
            </AccordionDetails>
          </Accordion>
        ))}
      </div>
    </div>
  );
};

// ------------------------
// CSV Export Helpers
// ------------------------
const buildTreatmentsByDeviceCsv = (treatments: Treatment[]): string => {
  let csv = '';

  const byDevice: Record<string, Treatment[]> = {};
  treatments.forEach((t) => {
    const deviceId = t.deviceId ?? 'UNKNOWN_DEVICE';
    if (!byDevice[deviceId]) byDevice[deviceId] = [];
    byDevice[deviceId].push(t);
  });

  Object.entries(byDevice).forEach(([deviceId, deviceTreatments]) => {
    deviceTreatments.sort(
      (a, b) => a.startTime.toMillis() - b.startTime.toMillis()
    );

    csv += `Device ID: ${deviceId}\n`;
    csv += [
      'Start Time (ISO)',
      'Length (mins)',
      'Temperature Pre',
      'Temperature Post',
      'Pain Score Pre',
      'Pain Score Post',
      'Device TN',
      'Platform',
    ].join(',') + '\n';

    deviceTreatments.forEach((t) => {
      csv += [
        t.startTime.toISO(),
        t.length ?? '',
        t.temperaturePre ?? '',
        t.temperaturePost ?? '',
        t.painScorePre ?? '',
        t.painScorePost ?? '',
        t.deviceTN ?? '',
        t.platform ?? '',
      ].join(',') + '\n';
    });

    csv += '\n';
  });

  return csv;
};

const buildTreatmentsTimelineCsv = (treatments: Treatment[]): string => {
  const sorted = [...treatments].sort(
    (a, b) => a.startTime.toMillis() - b.startTime.toMillis()
  );

  let csv =
    [
      'Start Time (ISO)',
      'Device ID',
      'Length (mins)',
      'Temperature Pre',
      'Temperature Post',
      'Pain Score Pre',
      'Pain Score Post',
      'Device TN',
      'Platform',
    ].join(',') + '\n';

  sorted.forEach((t) => {
    csv += [
      t.startTime.toISO(),
      t.deviceId ?? '',
      t.length ?? '',
      t.temperaturePre ?? '',
      t.temperaturePost ?? '',
      t.painScorePre ?? '',
      t.painScorePost ?? '',
      t.deviceTN ?? '',
      t.platform ?? '',
    ].join(',') + '\n';
  });

  return csv;
};


export const downloadTreatmentsZip = async (treatments: Treatment[]) => {
  if (!treatments.length) return;

  // Normalize timestamps (shared logic)
  const normalized = treatments
    .map((t) => {
      let startTime = t.startTime;
      if (!DateTime.isDateTime(startTime)) {
        if (typeof startTime === 'string') {
          startTime = DateTime.fromISO(startTime);
        } else if (typeof startTime === 'number') {
          startTime = DateTime.fromSeconds(startTime);
        }
      }
      return startTime ? { ...t, startTime } : null;
    })
    .filter(Boolean) as Treatment[];

  const zip = new JSZip();

  zip.file(
    'treatments_by_device.csv',
    buildTreatmentsByDeviceCsv(normalized)
  );

  zip.file(
    'treatments_timeline.csv',
    buildTreatmentsTimelineCsv(normalized)
  );

  const blob = await zip.generateAsync({ type: 'blob' });

  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `treatments_export_${DateTime.now().toFormat(
    'yyyyLLdd_HHmm'
  )}.zip`;

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export default TreatmentTimeline;